/* Bellar Hair booking — bundled build. Do not edit by hand;
   edit the source in js/ and re-run build/build-shopify.py. */
(function () {
'use strict';

/* ===== js/data/config.js ===== */

/**
 * Bellar Hair — Booking Platform Configuration
 * -------------------------------------------------
 * Single source of truth for business rules. Everything the salon owner
 * would realistically need to tune lives here so no code changes are needed
 * to adjust hours, deposit %, policies, or branding.
 *
 * This file is intentionally framework-free and dependency-free so it can be
 * dropped into a Shopify theme (as a `assets/*.js` module) or a standalone
 * static host without a build step.
 */

const BUSINESS = {
  name: 'Bellar Hair',
  tagline: 'Slay. Smile. Repeat.',
  parentBrand: 'Rockland Beauty Supply',
  website: 'https://www.bellarhair.ca',
  email: 'hello@bellarhair.ca',        // TODO: confirm real inbox
  phone: '613-446-4141',               // Rockland Beauty Supply line
  address: '2848 Laurier St, Rockland, ON K4K 1A3',
  currency: 'CAD',
  locale: 'en-CA',
  timezone: 'America/Toronto',
  // Taxes are NOT included in listed prices (per the service menu).
  taxRate: 0.13,                       // ON HST — shown as an estimate only
  taxLabel: 'HST (13%)',
};

/**
 * Booking mode — the single switch between the two ways to book.
 *
 *   'request'  → NO online payment. The client confirms their selection, we
 *                show the estimated total and EMAIL them a summary, and they
 *                pay in-store on the day. Cheapest to run (no processor at all).
 *   'deposit'  → The client pays a deposit now to confirm (needs a payment
 *                processor — Shopify Payments if embedded, or Stripe if
 *                standalone). Best for cutting no-shows.
 *
 * Flip this one value to switch the whole final step and confirmation copy.
 */
const BOOKING = {
  mode: 'request',                       // 'request' | 'deposit'
  fromName: 'Bellar Hair',
  // Where a copy of each booking summary is also sent (the salon's inbox).
  notifyOwnerTo: 'hello@bellarhair.ca',  // TODO: confirm real inbox
};

/** Convenience used across views + controller: are we in no-payment mode? */
const isRequestMode = () => BOOKING.mode === 'request';

/**
 * Staff mode — lets a service agent taking the booking (in-store or by phone)
 * override the appointment duration by hand, which the calendar and the
 * booking record then use. Vital for the open-ended "custom" service and
 * variable revamps where the auto-estimate isn't right.
 *
 * Turned on with `?staff=1` in the URL (e.g. book on a tablet at the desk).
 * This is a lightweight MVP gate — before production, put the staff view behind
 * real authentication or on a separate, access-controlled page so clients can't
 * simply add the query string.
 */
const STAFF = {
  urlParam: 'staff',      // ?staff=1 enables it
  minDurationMin: 15,     // guardrails for a hand-typed duration
  maxDurationMin: 600,
};

/**
 * Confirmation email via EmailJS — the fully standalone route (no backend).
 * Paste the three values from your EmailJS dashboard and emails start sending;
 * until then the app falls back to the on-screen mock automatically.
 *
 * Setup (one time):
 *   1. Create a free EmailJS account and connect an email service.
 *   2. Make a template whose variables match `templateParams` in notify.js
 *      (to_email, to_name, reference, appt_when, summary_text, owner_email…).
 *   3. Copy your Public Key, Service ID, and Template ID below.
 * The public key is safe to expose in the browser — that's by design.
 */
const EMAILJS = {
  publicKey: '2zhxlVlJA4hd952Sy',   // public by design (safe in browser)
  serviceId: 'service_oxfxwnb',
  templateId: 'template_pv8uta4',
};

/**
 * Supabase — the shared booking backend (so availability + double-booking
 * protection work across ALL customers and devices, not just one browser).
 *
 * Fill both values and the app switches from browser-only localStorage to the
 * shared database automatically; leave them blank to stay on localStorage.
 *
 * Both values are safe to expose in the browser — the anon key is public by
 * design. Security comes from the Row Level Security policies in
 * supabase/schema.sql: the public can INSERT a booking and read only
 * non-personal availability (date + time + length) — never other customers'
 * names, emails, or phone numbers. Run that SQL once in your Supabase project.
 */
const SUPABASE = {
  url: 'https://oivvudwkortommylubex.supabase.co',
  anonKey: 'sb_publishable_8Qg5T8mrbwnP4SmPry-Dmg_-EC4lA1Q', // public (safe in browser)
};

/**
 * Deposit policy (used only when BOOKING.mode === 'deposit').
 * Research benchmark (2025/26): a 25% deposit is the North-American
 * mid-ticket salon standard, paired with 48h cancellation notice.
 * `type: 'percent'` uses `value` as a fraction of the service subtotal.
 * Switch to `type: 'flat'` with `value: 25` for a fixed $25 deposit.
 */
const DEPOSIT = {
  type: 'percent',       // 'percent' | 'flat'
  value: 0.25,           // 25% of subtotal (or a flat dollar amount if 'flat')
  min: 20,               // never charge less than this deposit (CAD)
  refundableNoticeHours: 48,
  noShowFeePct: 0.5,     // 50% of service price on no-show / late cancel
};

/**
 * Length surcharge — closes the "unbilled revamp" leak.
 * Any service flagged `lengthSensitive` gets this surcharge added when the
 * client selects 24"+ hair. Owner can change the amount in one place.
 */
const LENGTH_SURCHARGE = {
  thresholdInches: 24,
  amount: 25,            // CAD added per length-sensitive service for 24"+
  label: '24″ and above',
};

/**
 * Business hours by weekday (0 = Sunday … 6 = Saturday).
 * `null` = closed. Times are 24h "HH:MM" in the business timezone.
 * Mirrors Rockland Beauty Supply retail hours; adjust freely.
 */
const HOURS = {
  0: null,                          // Sunday — closed
  1: { open: '09:30', close: '18:00' },
  2: { open: '09:30', close: '18:00' },
  3: { open: '09:30', close: '18:00' },
  4: { open: '09:30', close: '18:00' },
  5: { open: '09:30', close: '18:00' },
  6: { open: '09:30', close: '17:00' }, // Saturday
};

const SCHEDULING = {
  slotIntervalMinutes: 30,   // granularity of start times shown
  bufferMinutes: 15,         // clean-up/setup gap enforced after each booking
  minLeadHours: 12,          // earliest a client can book from "now"
  maxAdvanceDays: 60,        // how far ahead the calendar opens
  concurrentCapacity: 1,     // chairs/stylists working in parallel
};

/**
 * Brand tokens are duplicated in css/tokens.css as CSS variables.
 * Kept here too so any JS-rendered (e.g. canvas/share) assets stay on-brand.
 */
const BRAND = {
  ink: '#0A0A0A',
  gold: '#C9A24B',
  goldLight: '#E7C873',
  goldDeep: '#9C7A2E',
};

const POLICY_TEXT = {
  deposit:
    'A 25% deposit is required to confirm your appointment and is applied to ' +
    'your final balance. The remaining balance is due in-store on the day of ' +
    'your appointment.',
  request:
    'No payment is needed to book. We\'ll email you a summary with your ' +
    'estimated total, and you settle in-store on the day of your appointment.',
  quote:
    'Items marked “to be quoted” are priced in-store once we see your hair and ' +
    'confirm exactly what you need — so your estimated total may rise slightly.',
  instructions:
    'Anything you add here may attract an additional charge, confirmed with ' +
    'you before we begin. It won\'t change your estimated total above until we ' +
    'agree on it in-store.',
  cancellation:
    'Please give at least 48 hours notice to reschedule or cancel. ' +
    'Deposits for late cancellations or no-shows are non-refundable, and a ' +
    '50% no-show fee may apply to future bookings.',
  taxes: 'All prices are in CAD. Taxes are not included in listed prices.',
  length:
    'Hair 24″ and above attracts an additional charge, applied automatically ' +
    'when you select the longer length option.',
};

/* ===== js/data/services.js ===== */

/**
 * Bellar Hair — Service Catalog
 * -------------------------------------------------
 * Transcribed from the official Services Price List (CAD, taxes excl.).
 *
 * Each service is a structured line item so nothing gets left "unbilled":
 *   - price / priceType ('fixed' or 'from' for **-marked variable services)
 *   - durationMin      (used by the availability engine to fit real slots)
 *   - lengthSensitive  (adds the 24″+ surcharge automatically)
 *   - category         (drives grouping in the UI)
 *
 * Add-ons are separate so clients consciously attach billable extras
 * (elastic band/comb, plucking & tinting) that historically slipped through.
 *
 * To edit the menu, change this file only — the UI, totals, and scheduling
 * all read from here. This structure maps 1:1 to Shopify products/variants
 * for a future migration (see SHOPIFY_INTEGRATION.md).
 */

const CATEGORIES = [
  { id: 'wigging',     name: 'Wigging',              blurb: 'Machine-sewn wig construction, made to slay.' },
  { id: 'revamping',   name: 'Revamping',            blurb: 'Bring a tired wig back to life.' },
  { id: 'additional',  name: 'Additional Services',  blurb: 'Finishing touches & repairs.' },
  { id: 'custom',      name: 'Not sure? Tell us',    blurb: 'Describe it and we\'ll confirm the price with you.' },
];

const SERVICES = [
  // ── WIGGING ──────────────────────────────────────────────
  {
    id: 'closure-wig',
    category: 'wigging',
    name: 'Closure Wig Making',
    price: 100, priceType: 'fixed', durationMin: 120,
    lengthSensitive: true,
    desc: 'Custom machine-sewn closure wig, built to your cap size and density.',
  },
  {
    id: 'frontal-wig',
    category: 'wigging',
    name: 'Frontal Wig Making',
    price: 130, priceType: 'fixed', durationMin: 150,
    lengthSensitive: true,
    desc: 'Full lace frontal wig with a natural, versatile hairline.',
  },
  {
    id: 'upart-wig',
    category: 'wigging',
    name: 'U-Part Wig Making',
    price: 100, priceType: 'fixed', durationMin: 120,
    lengthSensitive: true,
    desc: 'Protective U-part wig that blends with your leave-out.',
  },

  // ── REVAMPING ────────────────────────────────────────────
  {
    id: 'wig-reconstruction',
    category: 'revamping',
    name: 'Wig Reconstruction & Treatment',
    price: 165, priceType: 'fixed', durationMin: 120,
    lengthSensitive: true,
    desc: 'Deep repair and treatment to rebuild a well-loved wig.',
  },
  {
    id: 'regal-treatment',
    category: 'revamping',
    name: 'Regal Wig Treatment (Bouncy & Wavy)',
    price: 75, priceType: 'fixed', durationMin: 90,
    lengthSensitive: true,
    desc: 'Restores bounce and body for a soft, wavy finish.',
  },
  {
    id: 'curl-rejuvenation',
    category: 'revamping',
    name: 'Curl Rejuvenation Treatment (Curly)',
    price: 75, priceType: 'fixed', durationMin: 90,
    lengthSensitive: true,
    desc: 'Revives definition and moisture in curly units.',
  },
  {
    id: 'silk-infusion',
    category: 'revamping',
    name: 'Silk Infusion Treatment & Silk Press',
    price: 75, priceType: 'fixed', durationMin: 90,
    lengthSensitive: true,
    desc: 'Smoothing silk treatment finished with a sleek silk press.',
  },
  {
    id: 'cowashing',
    category: 'revamping',
    name: 'Cowashing Only',
    price: 55, priceType: 'fixed', durationMin: 45,
    lengthSensitive: false,
    desc: 'Gentle conditioning wash to refresh your unit.',
  },
  {
    id: 'wig-styling',
    category: 'revamping',
    name: 'Wig Styling Only',
    price: 45, priceType: 'fixed', durationMin: 45,
    lengthSensitive: false,
    desc: 'Professional styling to get you camera-ready.',
  },

  // ── ADDITIONAL SERVICES ──────────────────────────────────
  {
    id: 'layering',
    category: 'additional',
    name: 'Layering',
    price: 90, priceType: 'from', durationMin: 60,
    lengthSensitive: false,
    desc: 'Custom layers cut into your unit. Final price confirmed in-store.',
  },
  {
    id: 'wig-repair',
    category: 'additional',
    name: 'Wig Repair',
    price: 35, priceType: 'from', durationMin: 30,
    lengthSensitive: false,
    desc: 'Fix shedding, lifting lace, or loose wefts. Priced by condition.',
  },

  // Educational services (one-on-one / digital tutorials) removed 2026-07-31 to
  // match the updated price list. Re-add a { category: 'educational' } entry
  // here (and its CATEGORIES row) if tutorials return to the menu.

  // ── CUSTOM / OPEN-ENDED ──────────────────────────────────
  // The catch-all for clients (especially revamps) who aren't sure which
  // service they need. priceType 'quote' means the price is confirmed in-store
  // rather than shown up front; selecting it reveals a required "what do you
  // need?" field. durationMin reserves a sensible placeholder in the calendar.
  {
    id: 'custom-request',
    category: 'custom',
    name: 'Something else / Not sure',
    price: 0, priceType: 'quote', durationMin: 60,
    lengthSensitive: false,
    custom: true,
    desc: 'Tell us what you\'d like done and we\'ll confirm the price and time with you.',
  },
];

/**
 * Add-ons attach to a booking as explicit billable line items.
 * `attachesTo: 'wigging'` hints the UI to surface these with wig services,
 * but the client can add them to any booking.
 */
const ADDONS = [
  {
    id: 'elastic-band-comb',
    name: 'Add Elastic Band or Comb',
    price: 10, durationMin: 15,
    desc: 'Extra security and a snug, comfortable fit.',
    attachesTo: 'wigging',
  },
  {
    id: 'plucking-tinting',
    name: 'Plucking Knots & Lace Tinting',
    price: 35, durationMin: 30,
    desc: 'Bleached knots plucked and lace tinted for the most natural melt.',
    attachesTo: 'wigging',
  },
];

// Lookup helpers ------------------------------------------------------------
const _byId = Object.fromEntries(SERVICES.map((s) => [s.id, s]));
const _addonById = Object.fromEntries(ADDONS.map((a) => [a.id, a]));

const getService = (id) => _byId[id];
const getAddon = (id) => _addonById[id];
const servicesByCategory = (catId) =>
  SERVICES.filter((s) => s.category === catId);

/* ===== js/lib/dates.js ===== */

/**
 * Date & time helpers. Kept small and dependency-free.
 * All calendar math works in local calendar days (YYYY-MM-DD keys) and
 * "HH:MM" clock strings — deliberately timezone-simple for an MVP. The
 * availability engine treats these as the business's local wall-clock time.
 */


/** "YYYY-MM-DD" for a Date (local). */
function dateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse "YYYY-MM-DD" to a local Date at midnight. */
function fromKey(key) {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

const minutesToHM = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const hmToMinutes = (hm) => {
  const [h, m] = hm.split(':').map(Number);
  return h * 60 + m;
};

/** Human 12h label, e.g. "9:30 AM". */
function label12h(hm) {
  const [h, m] = hm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

/** e.g. "Fri, Aug 1". */
function labelDate(key) {
  return fromKey(key).toLocaleDateString(BUSINESS.locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** e.g. "Friday, August 1, 2026". */
function labelDateLong(key) {
  return fromKey(key).toLocaleDateString(BUSINESS.locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const isSameDay = (a, b) => dateKey(a) === dateKey(b);

/* ===== js/lib/money.js ===== */

/**
 * Money & pricing helpers — the single place cart math happens.
 * Keeping all totals here means the UI and confirmation email/receipt can
 * never disagree about what a booking costs.
 */



const fmt = new Intl.NumberFormat(BUSINESS.locale, {
  style: 'currency',
  currency: BUSINESS.currency,
});

const money = (n) => fmt.format(n);

/** Round to cents to avoid floating-point drift in totals. */
const cents = (n) => Math.round(n * 100) / 100;

/**
 * Build a full price breakdown from the current selection.
 * @param {object} sel
 * @param {string[]} sel.serviceIds
 * @param {string[]} sel.addonIds
 * @param {boolean}  sel.isLong        // 24"+ length selected
 * @returns {object} line items + subtotal + deposit + estimated tax/total
 */
function priceBooking({ serviceIds = [], addonIds = [], isLong = false }) {
  const lines = [];
  let hasFromPrice = false;
  let hasQuote = false;

  for (const id of serviceIds) {
    const s = getService(id);
    if (!s) continue;
    if (s.priceType === 'from') hasFromPrice = true;
    const isQuote = s.priceType === 'quote';
    if (isQuote) hasQuote = true;
    lines.push({
      id: s.id,
      label: s.name,
      amount: isQuote ? 0 : cents(s.price),  // quote items don't move the subtotal
      kind: 'service',
      quote: isQuote,                        // views render "to be quoted"
      note: s.priceType === 'from' ? 'from' : null,
    });
    if (isLong && s.lengthSensitive) {
      lines.push({
        id: `${s.id}--length`,
        label: `↳ ${LENGTH_SURCHARGE.label} surcharge`,
        amount: cents(LENGTH_SURCHARGE.amount),
        kind: 'surcharge',
        note: null,
      });
    }
  }

  for (const id of addonIds) {
    const a = getAddon(id);
    if (!a) continue;
    lines.push({
      id: a.id,
      label: a.name,
      amount: cents(a.price),
      kind: 'addon',
      note: null,
    });
  }

  const subtotal = cents(lines.reduce((sum, l) => sum + l.amount, 0));

  // Deposit
  let deposit =
    DEPOSIT.type === 'percent' ? cents(subtotal * DEPOSIT.value) : cents(DEPOSIT.value);
  deposit = Math.min(Math.max(deposit, DEPOSIT.min), subtotal); // clamp to [min, subtotal]
  deposit = cents(deposit);

  const balanceDue = cents(subtotal - deposit);
  const estTax = cents(subtotal * BUSINESS.taxRate);
  const estTotal = cents(subtotal + estTax);

  return {
    lines,
    subtotal,
    deposit,
    balanceDue,
    estTax,
    estTotal,
    hasFromPrice, // true => show "final price confirmed in-store" note
    hasQuote,     // true => a custom/quote item is in the cart
    depositLabel:
      DEPOSIT.type === 'percent'
        ? `${Math.round(DEPOSIT.value * 100)}% deposit`
        : 'Deposit',
  };
}

/* ===== js/lib/availability.js ===== */

/**
 * Availability engine
 * -------------------------------------------------
 * Generates real, bookable start-times for a given day from:
 *   - business hours for that weekday
 *   - the total duration of the selected services (+ buffer)
 *   - existing bookings (so we never double-book beyond capacity)
 *   - lead-time and max-advance rules
 *
 * This is pure logic with no DOM/storage coupling: pass in the day and the
 * existing bookings, get back an array of slots. That makes it trivial to
 * swap the booking source for Supabase/Shopify later (see store/store.js).
 */



/** Total minutes a booking occupies, including the clean-up buffer. */
function bookingDuration(totalServiceMinutes) {
  return totalServiceMinutes + SCHEDULING.bufferMinutes;
}

/**
 * @param {string} key           "YYYY-MM-DD"
 * @param {number} serviceMinutes total duration of selected services
 * @param {Array}  bookings       [{ date, start (HH:MM), durationMin }]
 * @param {Date}   now            injectable clock (defaults to real now)
 * @returns {Array<{start:string,label:string,disabled:boolean}>}
 */
function slotsForDay(key, serviceMinutes, bookings = [], now = new Date()) {
  const day = fromKey(key);
  const weekday = day.getDay();
  const hours = HOURS[weekday];
  if (!hours) return []; // closed

  const need = bookingDuration(serviceMinutes);
  const openMin = hmToMinutes(hours.open);
  const closeMin = hmToMinutes(hours.close);
  const interval = SCHEDULING.slotIntervalMinutes;

  // Occupied [start,end) windows for this day, with buffer already baked into
  // each stored booking's duration.
  const occupied = bookings
    .filter((b) => b.date === key)
    .map((b) => {
      const s = hmToMinutes(b.start);
      return [s, s + b.durationMin];
    });

  const leadCutoff = new Date(now.getTime() + SCHEDULING.minLeadHours * 3600 * 1000);
  const todayKey = dateKey(now);

  const slots = [];
  for (let t = openMin; t + need <= closeMin; t += interval) {
    const start = minutesToHM(t);
    let disabled = false;

    // Lead-time rule: if booking is for today (or the cutoff spills into it),
    // the slot's start must be after the lead cutoff.
    if (key === todayKey || key === dateKey(leadCutoff)) {
      const slotStart = fromKey(key);
      slotStart.setHours(Math.floor(t / 60), t % 60, 0, 0);
      if (slotStart < leadCutoff) disabled = true;
    }

    // Capacity rule: count overlaps; block once we hit concurrentCapacity.
    if (!disabled) {
      const end = t + need;
      const overlaps = occupied.filter(([os, oe]) => t < oe && os < end).length;
      if (overlaps >= SCHEDULING.concurrentCapacity) disabled = true;
    }

    slots.push({ start, label: start, disabled });
  }
  return slots;
}

/** True if a day has at least one open (non-disabled) slot. */
function dayHasAvailability(key, serviceMinutes, bookings, now = new Date()) {
  return slotsForDay(key, serviceMinutes, bookings, now).some((s) => !s.disabled);
}

/** Is this calendar day within the bookable window and an open weekday? */
function isDayBookable(key, now = new Date()) {
  const day = fromKey(key);
  if (!HOURS[day.getDay()]) return false;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const max = new Date(today);
  max.setDate(max.getDate() + SCHEDULING.maxAdvanceDays);
  return day >= today && day <= max;
}

/* ===== js/store/store.js ===== */

/**
 * BookingStore — the data-access seam.
 * -------------------------------------------------
 * The whole app talks to ONE async interface:
 *
 *     listBookings(fromKey, toKey) -> [{ date, start, durationMin, ... }]
 *     createBooking(booking)       -> booking (with id + status)
 *     markDepositPaid(id, payment) -> booking
 *
 * Two implementations ship: `LocalStore` (browser localStorage, zero infra) and
 * `SupabaseStore` (a real shared backend so availability + double-booking
 * protection work across ALL customers). `activeStore` auto-selects Supabase
 * when its keys are configured, else localStorage.
 */



const LS_KEY = 'bellar.bookings.v1';

function uid() {
  return 'BH-' + Date.now().toString(36).toUpperCase() +
    '-' + Math.random().toString(36).slice(2, 6).toUpperCase();
}

/** LocalStorage-backed store (default for the MVP). */
const LocalStore = {
  async listBookings(fromKey, toKey) {
    const all = _readAll();
    return all.filter((b) => (!fromKey || b.date >= fromKey) && (!toKey || b.date <= toKey));
  },

  async createBooking(booking) {
    const all = _readAll();
    const record = {
      ...booking,
      id: uid(),
      // Deposit-mode bookings start unpaid; request-mode bookings are confirmed
      // on creation (no payment gate). The caller sets this via the draft.
      status: booking.status || 'pending_deposit',
      createdAt: new Date().toISOString(),
    };
    all.push(record);
    localStorage.setItem(LS_KEY, JSON.stringify(all));
    return record;
  },

  async markDepositPaid(id, payment) {
    const all = _readAll();
    const rec = all.find((b) => b.id === id);
    if (rec) {
      rec.status = 'confirmed';
      rec.payment = payment;
      localStorage.setItem(LS_KEY, JSON.stringify(all));
    }
    return rec;
  },
};

function _readAll() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '[]');
  } catch {
    return [];
  }
}

/**
 * ── Supabase-backed store (shared, real backend) ────────────────────
 * Uses the supabase-js SDK (loaded from a CDN in index.html / the Shopify
 * section) via `window.supabase`. Reads availability through a SECURITY DEFINER
 * RPC that returns ONLY non-personal columns (date/time/length) — so the public
 * anon key can never read customers' names, emails, or phones. Writes go
 * through an insert-only RLS policy. See supabase/schema.sql.
 */
let _sb = null;
function sb() {
  if (_sb) return _sb;
  if (!window.supabase || !window.supabase.createClient) {
    throw new Error('Supabase SDK not loaded');
  }
  _sb = window.supabase.createClient(SUPABASE.url, SUPABASE.anonKey);
  return _sb;
}

// Map our booking record → the flat DB row (snake_case columns).
function toRow(r) {
  return {
    id: r.id,
    date: r.date,
    start_time: r.start,
    duration_min: r.durationMin,
    duration_override: r.durationOverride ?? null,
    booked_by: r.bookedBy || 'client',
    service_ids: r.serviceIds || [],
    addon_ids: r.addonIds || [],
    is_long: !!r.isLong,
    custom_request: r.customRequest || null,
    notes: r.notes || null,
    customer: r.customer || {},
    pricing: r.pricing || {},
    currency: r.currency || 'CAD',
    mode: r.mode || null,
    status: r.status || 'pending_deposit',
  };
}

const SupabaseStore = {
  // Availability only — non-personal columns via the RPC. Defaults to the whole
  // bookable window so the client can filter days locally like LocalStore does.
  async listBookings(fromKey, toKey) {
    const from = fromKey || dateKey(addDays(new Date(), -1));
    const to = toKey || dateKey(addDays(new Date(), 120));
    try {
      const { data, error } = await sb().rpc('get_availability', { from_date: from, to_date: to });
      if (error) throw error;
      return (data || []).map((row) => ({
        date: row.date,
        start: row.start_time,
        durationMin: row.duration_min,
      }));
    } catch (err) {
      console.error('[Bellar] availability fetch failed:', err.message || err);
      return []; // degrade to "all open" rather than block booking
    }
  },

  async createBooking(booking) {
    const record = {
      ...booking,
      id: uid(),
      status: booking.status || 'pending_deposit',
      createdAt: new Date().toISOString(),
    };
    const { error } = await sb().from('bookings').insert(toRow(record));
    if (error) throw error;
    return record;
  },

  // Deposit mode only. Client-side update needs an UPDATE policy (see the SQL);
  // for real payments confirm server-side via a webhook. Request mode (default)
  // never calls this, so the secure insert-only setup is enough on its own.
  async markDepositPaid(id, payment) {
    try {
      const { error } = await sb().from('bookings')
        .update({ status: 'confirmed', payment }).eq('id', id);
      if (error) throw error;
    } catch (err) {
      console.warn('[Bellar] markDepositPaid needs an UPDATE policy:', err.message || err);
    }
    return { id, status: 'confirmed', payment };
  },
};

/**
 * ── Shopify store (blueprint) ───────────────────────────────────────
 * Bookings become draft orders / metaobjects via the Admin API (from a small
 * server), and the deposit is a Shopify Checkout for a "Deposit" product.
 * See SHOPIFY_INTEGRATION.md for the full mapping.
 */

// Use the shared Supabase backend once its keys are set; otherwise fall back to
// browser-only localStorage so the app still runs during setup.
const supabaseConfigured = !!(SUPABASE.url && SUPABASE.anonKey);
const activeStore = supabaseConfigured ? SupabaseStore : LocalStore;

/* ===== js/store/payments.js ===== */

/**
 * Payment adapter for the deposit step.
 * -------------------------------------------------
 * The UI calls `payDeposit({ amount, currency, booking })` and awaits a
 * result `{ ok, reference, method }`. That's the only contract.
 *
 * MVP: `MockPay` simulates a successful deposit so the full flow is
 * demonstrable end-to-end WITHOUT exposing any secret keys in a static app
 * (which would be unsafe). Real card capture must go through a hosted
 * checkout — never collect raw card fields in this front-end.
 *
 * Going live: implement `payDeposit` to create a Stripe Checkout Session
 * (or Payment Link, or Shopify Checkout for a "Deposit" product) on a small
 * server/edge function and redirect to it. Blueprint at the bottom.
 */

const MockPay = {
  label: 'Demo checkout (no real charge)',
  async payDeposit({ amount, currency }) {
    // Simulate network + processing latency.
    await new Promise((r) => setTimeout(r, 900));
    return {
      ok: true,
      method: 'mock',
      reference: 'DEMO-' + Math.random().toString(36).slice(2, 10).toUpperCase(),
      amount,
      currency,
    };
  },
};

/**
 * ── Stripe blueprint (server-backed; do NOT put secret keys here) ──
 *
 * export const StripePay = {
 *   label: 'Card via Stripe',
 *   async payDeposit({ amount, currency, booking }) {
 *     // POST to your own endpoint that holds the Stripe secret key.
 *     const res = await fetch('/api/create-deposit-session', {
 *       method: 'POST',
 *       headers: { 'Content-Type': 'application/json' },
 *       body: JSON.stringify({ amount, currency, booking }),
 *     });
 *     const { url } = await res.json();
 *     window.location.href = url;   // hosted Stripe Checkout
 *     return new Promise(() => {}); // navigation takes over
 *   },
 * };
 */

const activePayment = MockPay;

/* ===== js/store/notify.js ===== */

/**
 * Notification adapter — sends the booking summary to the client (and a copy
 * to the salon). Same swap-in seam as the store and payment adapters: the app
 * calls one method and awaits a result.
 *
 *     sendConfirmation({ booking, to, ownerTo, html, text }) -> { ok, channel }
 *
 * MVP: `MockNotify` simulates a successful send so the no-payment flow works
 * end-to-end without wiring an email service. The confirmation screen tells the
 * client the summary was emailed.
 *
 * Going live, pick ONE of the blueprints below:
 *   - EmailJS  → send straight from the browser (true static, ~free tier).
 *                Good enough for confirmations; watch deliverability.
 *   - Resend / Postmark / SendGrid → send from a tiny server/edge endpoint.
 *                Best deliverability; keep the API key on the server.
 *   - Shopify  → if embedded, trigger Shopify's transactional email/Flow on
 *                the order/metaobject created in store.js.
 */


const MockNotify = {
  label: 'Demo email (not actually sent)',
  async sendConfirmation({ to }) {
    await new Promise((r) => setTimeout(r, 700));
    return { ok: true, channel: 'mock', to };
  },
};

/**
 * EmailJS — pure static, no backend. Sends the confirmation straight from the
 * browser using the salon's EmailJS account. Configure keys in config.js →
 * EMAILJS and add the SDK <script> in index.html (already included).
 *
 * The template in your EmailJS dashboard should reference these variables:
 *   {{to_email}} {{to_name}} {{reference}} {{appt_when}} {{summary_text}}
 *   {{owner_email}}   (set the template's "To" to {{to_email}} and, if you want
 *                      a salon copy, add {{owner_email}} as Bcc/Cc).
 */
const EmailJsNotify = {
  label: 'EmailJS (standalone)',
  async sendConfirmation({ booking, to, ownerTo, text }) {
    if (!window.emailjs) throw new Error('EmailJS SDK not loaded');
    const params = {
      to_email: to,
      to_name: booking?.customer?.name || '',
      reference: booking?.id || '',
      appt_when: `${booking?.date || ''} ${booking?.start || ''}`.trim(),
      summary_text: text,
      summary_html: String(text).replace(/\n/g, '<br>'), // for HTML email templates
      owner_email: ownerTo || '',
    };
    await window.emailjs.send(EMAILJS.serviceId, EMAILJS.templateId, params, {
      publicKey: EMAILJS.publicKey,
    });
    return { ok: true, channel: 'emailjs', to };
  },
};

/**
 * ── Server blueprint (Resend/Postmark/etc.) — for later, if you outgrow
 * standalone email and want better deliverability. Send from a tiny endpoint
 * that holds the API key; never expose a secret key in the browser.
 *
 * export const ServerNotify = {
 *   async sendConfirmation(payload) {
 *     const res = await fetch('/api/send-confirmation', {
 *       method: 'POST', headers: { 'Content-Type': 'application/json' },
 *       body: JSON.stringify(payload),
 *     });
 *     return res.ok ? { ok: true, channel: 'server' } : { ok: false };
 *   },
 * };
 */

// Use EmailJS once its three keys are filled in; otherwise fall back to the
// on-screen mock so the flow still works end-to-end during setup.
const emailjsConfigured = !!(EMAILJS.publicKey && EMAILJS.serviceId && EMAILJS.templateId);
const activeNotify = emailjsConfigured ? EmailJsNotify : MockNotify;

/**
 * Build a plain-text summary of a booking — reused for the email body and as a
 * fallback. Kept here so the email and the on-screen receipt never disagree.
 */
function bookingSummaryText({ business, dateLabel, timeLabel, lines, price, customRequest, notes, customer }) {
  const rows = lines.map((l) =>
    `  • ${l.label}${l.quote ? ' — to be quoted in-store' : ' — ' + fmtMoney(l.amount, business.currency)}`
  );
  const out = [
    `${business.name} — Booking summary`,
    ``,
    `Name:  ${customer.name}`,
    `When:  ${dateLabel} at ${timeLabel}`,
    `Where: ${business.address}`,
    ``,
    `Services:`,
    ...rows,
  ];
  if (customRequest) out.push(``, `Your request: ${customRequest}`);
  if (notes) out.push(``, `Further instructions: ${notes}`, `(may attract an additional charge, confirmed in-store)`);
  out.push(
    ``,
    `Estimated subtotal: ${fmtMoney(price.subtotal, business.currency)}` +
      (price.hasQuote ? ' + items to be quoted in-store' : ''),
    `Payment: none required now — pay in-store on the day.`,
  );
  return out.join('\n');
}

function fmtMoney(n, currency) {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(n);
}

/* ===== js/views.js ===== */

/**
 * Views — pure render functions that return HTML strings.
 * No state is mutated here; app.js owns state and wires up events after
 * injecting this markup. Keeping rendering pure makes the flow easy to reason
 * about and to port into a Shopify section later.
 */






const STEPS = ['Services', 'Options', 'Date & Time', 'Your Info', 'Deposit'];

const icons = {
  check: '<svg viewBox="0 0 24 24" fill="none" stroke="#1a1406" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>',
  clock: '<svg class="icon" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
  left:  '<svg class="icon" viewBox="0 0 24 24"><path d="m15 18-6-6 6-6"/></svg>',
  right: '<svg class="icon" viewBox="0 0 24 24"><path d="m9 18 6-6-6-6"/></svg>',
  shield:'<svg class="icon" viewBox="0 0 24 24"><path d="M12 3l7 3v5c0 4.4-3 8.3-7 10-4-1.7-7-5.6-7-10V6z"/></svg>',
  spark: '<svg viewBox="0 0 24 24" fill="none" stroke="#1a1406" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>',
};

const esc = (s) => String(s).replace(/[&<>"]/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const priceLabel = (s) =>
  s.priceType === 'quote'
    ? `<span class="pill">Custom quote</span>`
    : s.priceType === 'from'
    ? `${money(s.price)}<small> from</small>`
    : money(s.price);

// Render a line's amount, showing "Quoted" for custom/quote items.
const lineAmount = (l) =>
  l.quote ? `<span style="color:var(--gold)">Quoted in-store</span>` : money(l.amount);

/* ---- Stepper ---------------------------------------------- */
function renderStepper(current) {
  const labels = isRequestMode() ? [...STEPS.slice(0, 4), 'Confirm'] : STEPS;
  return `<div class="stepper">${labels.map((label, i) => {
    const cls = i === current ? 'is-active' : i < current ? 'is-done' : '';
    const dot = i < current ? icons.check : (i + 1);
    return `${i ? '<span class="stepper__sep"></span>' : ''}
      <div class="stepper__item ${cls}">
        <span class="stepper__dot">${dot}</span>
        <span class="stepper__label">${label}</span>
      </div>`;
  }).join('')}</div>`;
}

/* ---- Step 1: services ------------------------------------- */
function viewServices(state) {
  const cats = CATEGORIES.map((cat) => {
    const items = servicesByCategory(cat.id).map((s) => {
      const on = state.serviceIds.has(s.id);
      return `
        <button class="svc ${on ? 'is-selected' : ''}" data-service="${s.id}" aria-pressed="${on}">
          <div class="svc__top">
            <div>
              <div class="svc__name">${esc(s.name)}</div>
              <div class="svc__desc">${esc(s.desc)}</div>
            </div>
            <div class="svc__price">${priceLabel(s)}</div>
          </div>
          <div class="svc__meta">
            <span class="svc__check">${icons.check}</span>
            <span>${icons.clock} ~${s.durationMin} min</span>
            ${s.lengthSensitive ? '<span class="pill">24″+ surcharge applies</span>' : ''}
          </div>
        </button>`;
    }).join('');
    return `
      <section class="cat">
        <div class="cat__head"><h3>${esc(cat.name)}</h3><span>${esc(cat.blurb)}</span></div>
        <div class="svc-grid">${items}</div>
      </section>`;
  }).join('');

  return `
    <div class="step">
      <div class="step__head">
        <h2>Choose your services</h2>
        <p>Select everything you'd like in this visit — mix wig making, revamps, and finishing touches.</p>
      </div>
      ${cats}
    </div>`;
}

/* ---- Step 2: options -------------------------------------- */
function viewOptions(state) {
  const anyLengthSensitive = [...state.serviceIds].some((id) => getService(id)?.lengthSensitive);

  const lengthBlock = anyLengthSensitive ? `
    <div class="field">
      <label class="field__label">Hair length</label>
      <div class="choice-row">
        <button class="choice ${!state.isLong ? 'is-selected' : ''}" data-length="short">
          <div class="choice__title">Under 24″</div>
          <div class="choice__sub">Standard pricing</div>
        </button>
        <button class="choice ${state.isLong ? 'is-selected' : ''}" data-length="long">
          <div class="choice__title">${esc(LENGTH_SURCHARGE.label)}</div>
          <div class="choice__sub">+${money(LENGTH_SURCHARGE.amount)} per applicable service</div>
        </button>
      </div>
    </div>` : '';

  const addons = ADDONS.map((a) => {
    const on = state.addonIds.has(a.id);
    return `
      <button class="choice ${on ? 'is-selected' : ''}" data-addon="${a.id}" style="display:flex;justify-content:space-between;align-items:center;gap:14px">
        <span>
          <span class="choice__title">${esc(a.name)}</span>
          <span class="choice__sub">${esc(a.desc)}</span>
        </span>
        <span class="svc__price">${money(a.price)}</span>
      </button>`;
  }).join('');

  const auto = state.autoServiceMinutes;
  const override = state.durationOverride;
  const staffBlock = state.staff ? `
    <div class="field">
      <label class="field__label" for="staff-duration">Appointment duration <span class="pill">Staff</span></label>
      <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap">
        <input id="staff-duration" type="number" inputmode="numeric" min="${STAFF.minDurationMin}" max="${STAFF.maxDurationMin}" step="5" value="${override ?? ''}" placeholder="${auto}" style="max-width:130px">
        <span style="color:var(--text-mute);font-size:.85rem">minutes · auto estimate ${auto} min</span>
        ${override != null ? `<button class="btn btn--ghost" id="staff-duration-reset" style="padding:8px 14px">Reset to auto</button>` : ''}
      </div>
      <p class="summary__note">Set the real time for custom or complex work. Blank uses the ${auto}-minute estimate. This is what the calendar reserves for the appointment.</p>
    </div>` : '';

  const customSelected = state.serviceIds.has('custom-request');
  const customBlock = customSelected ? `
    <div class="field">
      <label class="field__label" for="custom-req">What would you like done? <span style="color:var(--danger)">*</span></label>
      <textarea id="custom-req" placeholder="e.g. My frontal is lifting and the curls have dropped — I'd like it refreshed and restyled. Not sure if that's a reconstruction or a treatment.">${esc(state.customRequest)}</textarea>
      <div class="input-err">${state.errors.customRequest || ''}</div>
    </div>` : '';

  return `
    <div class="step">
      <div class="step__head">
        <h2>Personalise it</h2>
        <p>A few details so nothing about your visit is left unbilled or unclear.</p>
      </div>
      ${staffBlock}
      ${lengthBlock}
      ${customBlock}
      <div class="field">
        <label class="field__label">Add-ons</label>
        <div class="addon-list">${addons}</div>
      </div>
      <div class="field">
        <label class="field__label" for="notes">Further instructions or special requests <span style="text-transform:none;color:var(--text-mute)">(optional)</span></label>
        <textarea id="notes" placeholder="Cap size, desired style, hair you're bringing, inspo links, or anything extra you'd like done…">${esc(state.notes)}</textarea>
        <p class="summary__note" style="margin-top:8px">${esc(POLICY_TEXT.instructions)}</p>
      </div>
    </div>`;
}

/* ---- Step 3: date & time ---------------------------------- */
function viewDateTime(state) {
  const totalMin = state.totalServiceMinutes;
  const month = state.calMonth;
  const first = startOfMonth(month);
  const startPad = first.getDay(); // 0 Sun … 6 Sat
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();

  const dow = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
    .map((d) => `<div class="cal__dow">${d}</div>`).join('');

  let cells = '';
  for (let i = 0; i < startPad; i++) cells += `<div class="cal__day is-empty"></div>`;
  for (let d = 1; d <= daysInMonth; d++) {
    const dd = new Date(month.getFullYear(), month.getMonth(), d);
    const key = dateKey(dd);
    const bookable = isDayBookable(key, state.now);
    const open = bookable && dayHasAvailability(key, totalMin, state.bookings, state.now);
    const selected = state.date === key;
    const cls = ['cal__day', open ? 'is-open' : '', selected ? 'is-selected' : ''].join(' ').trim();
    cells += `<button class="${cls}" data-day="${key}" ${bookable && open ? '' : 'disabled'}>${d}</button>`;
  }

  // Month nav bounds
  const now = state.now;
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const maxDay = addDays(now, SCHEDULING.maxAdvanceDays);
  const maxMonthStart = new Date(maxDay.getFullYear(), maxDay.getMonth(), 1);
  const prevDisabled = startOfMonth(month) <= thisMonthStart;
  const nextDisabled = startOfMonth(month) >= maxMonthStart;

  const monthTitle = month.toLocaleDateString(BUSINESS.locale, { month: 'long', year: 'numeric' });

  let slotsHtml = `<p class="slots__empty">Select an available date to see open times.</p>`;
  if (state.date) {
    const slots = slotsForDay(state.date, totalMin, state.bookings, state.now);
    const openSlots = slots.filter((s) => !s.disabled);
    if (openSlots.length === 0) {
      slotsHtml = `<p class="slots__empty">No open times on ${labelDate(state.date)} for a ${totalMin}-min visit. Try another day.</p>`;
    } else {
      slotsHtml = `<div class="slots__grid">${slots.map((s) => `
        <button class="slot ${state.slot === s.start ? 'is-selected' : ''}" data-slot="${s.start}" ${s.disabled ? 'disabled' : ''}>
          ${label12h(s.start)}
        </button>`).join('')}</div>`;
    }
  }

  return `
    <div class="step">
      <div class="step__head">
        <h2>Pick a date &amp; time</h2>
        <p>Live availability for a <strong>${totalMin}-minute</strong> visit, including set-up time.</p>
      </div>
      <div class="cal">
        <div class="cal__head">
          <div class="cal__title">${monthTitle}</div>
          <div class="cal__nav">
            <button data-month="-1" ${prevDisabled ? 'disabled' : ''} aria-label="Previous month">${icons.left}</button>
            <button data-month="1" ${nextDisabled ? 'disabled' : ''} aria-label="Next month">${icons.right}</button>
          </div>
        </div>
        <div class="cal__grid">${dow}${cells}</div>
      </div>
      <div class="slots">
        ${state.date ? `<label class="field__label">Times on ${labelDate(state.date)}</label>` : ''}
        ${slotsHtml}
      </div>
    </div>`;
}

/* ---- Step 4: your info ------------------------------------ */
function viewDetails(state) {
  const e = state.errors;
  return `
    <div class="step">
      <div class="step__head">
        <h2>Your details</h2>
        <p>We'll send your confirmation and reminders here.</p>
      </div>
      <div class="input-row">
        <div>
          <label class="input-label" for="c-name">Full name</label>
          <input id="c-name" type="text" autocomplete="name" value="${esc(state.customer.name)}" placeholder="Ella Bellar">
          <div class="input-err">${e.name || ''}</div>
        </div>
      </div>
      <div class="input-row cols-2">
        <div>
          <label class="input-label" for="c-email">Email</label>
          <input id="c-email" type="email" autocomplete="email" value="${esc(state.customer.email)}" placeholder="you@email.com">
          <div class="input-err">${e.email || ''}</div>
        </div>
        <div>
          <label class="input-label" for="c-phone">Mobile</label>
          <input id="c-phone" type="tel" autocomplete="tel" value="${esc(state.customer.phone)}" placeholder="(613) 555-0123">
          <div class="input-err">${e.phone || ''}</div>
        </div>
      </div>
    </div>`;
}

/* ---- Step 5: deposit / review ----------------------------- */
function viewDeposit(state) {
  const p = state.price;
  return `
    <div class="step">
      <div class="step__head">
        <h2>Confirm &amp; secure your spot</h2>
        <p>A ${p.depositLabel} holds your appointment. The balance is paid in-store.</p>
      </div>

      <div class="receipt">
        <h4>${labelDateLong(state.date)} · ${label12h(state.slot)}</h4>
        ${p.lines.map((l) => `
          <div class="receipt__row">
            <span class="k">${esc(l.label)}${l.note ? ` <small style="color:var(--gold)">(${l.note})</small>` : ''}</span>
            <span>${lineAmount(l)}</span>
          </div>`).join('')}
        <div class="receipt__row"><span class="k">Subtotal</span><span>${money(p.subtotal)}</span></div>
        <div class="receipt__row"><span class="k">${esc(BUSINESS.taxLabel)} (est.)</span><span>${money(p.estTax)}</span></div>
        <div class="receipt__row" style="font-weight:600;color:var(--gold-light)">
          <span>Due today — ${esc(p.depositLabel)}</span><span>${money(p.deposit)}</span>
        </div>
        <div class="receipt__row"><span class="k">Balance in-store</span><span>${money(p.balanceDue)}</span></div>
      </div>

      ${state.customRequest ? `
        <div class="field" style="margin-top:16px">
          <label class="field__label">Your request</label>
          <p style="color:var(--text-soft)">${esc(state.customRequest)}</p>
        </div>` : ''}

      ${state.notes ? `
        <div class="field" style="margin-top:16px">
          <label class="field__label">Further instructions</label>
          <p style="color:var(--text-soft)">${esc(state.notes)}</p>
          <p class="summary__note">${esc(POLICY_TEXT.instructions)}</p>
        </div>` : ''}

      <div class="field" style="margin-top:22px">
        <div class="summary__meta">${icons.shield}<span>${esc(POLICY_TEXT.cancellation)}</span></div>
        ${p.hasFromPrice || p.hasQuote ? `<p class="summary__note">Some items are priced “from” or quoted in-store based on your hair and desired result. Your deposit still applies to the balance.</p>` : ''}
      </div>

      <button class="btn btn--primary btn--block" id="pay-btn" ${state.paying ? 'disabled' : ''}>
        ${state.paying ? '<span class="spin"></span> Securing your spot…' : `Pay ${money(p.deposit)} deposit &amp; book`}
      </button>
      <p class="summary__note" style="text-align:center">${esc(POLICY_TEXT.taxes)} Demo checkout — no real charge is made.</p>
    </div>`;
}

/* ---- Step 5 (request mode): review + confirm, NO payment -- */
function viewConfirm(state) {
  const p = state.price;
  return `
    <div class="step">
      <div class="step__head">
        <h2>Review &amp; confirm your booking</h2>
        <p>No payment needed now — we'll email your summary and you settle in-store.</p>
      </div>

      <div class="receipt">
        <h4>${labelDateLong(state.date)} · ${label12h(state.slot)}</h4>
        ${p.lines.map((l) => `
          <div class="receipt__row">
            <span class="k">${esc(l.label)}${l.note ? ` <small style="color:var(--gold)">(${l.note})</small>` : ''}</span>
            <span>${lineAmount(l)}</span>
          </div>`).join('')}
        <div class="receipt__row"><span class="k">Estimated subtotal</span><span>${money(p.subtotal)}${p.hasQuote ? ' +' : ''}</span></div>
        <div class="receipt__row"><span class="k">${esc(BUSINESS.taxLabel)} (est.)</span><span>${money(p.estTax)}</span></div>
        <div class="receipt__row" style="font-weight:600;color:var(--gold-light)">
          <span>Estimated total${p.hasQuote ? ' (before quote)' : ''}</span><span>${money(p.estTotal)}</span>
        </div>
        <div class="receipt__row"><span class="k">Due today</span><span>$0.00 — pay in-store</span></div>
      </div>

      ${state.customRequest ? `
        <div class="field" style="margin-top:16px">
          <label class="field__label">Your request</label>
          <p style="color:var(--text-soft)">${esc(state.customRequest)}</p>
        </div>` : ''}

      ${state.notes ? `
        <div class="field" style="margin-top:16px">
          <label class="field__label">Further instructions</label>
          <p style="color:var(--text-soft)">${esc(state.notes)}</p>
          <p class="summary__note">${esc(POLICY_TEXT.instructions)}</p>
        </div>` : ''}

      <div class="field" style="margin-top:22px">
        <div class="summary__meta">${icons.shield}<span>${esc(POLICY_TEXT.request)}</span></div>
        ${p.hasQuote ? `<p class="summary__note">${esc(POLICY_TEXT.quote)}</p>` : ''}
      </div>

      <button class="btn btn--primary btn--block" id="confirm-btn" ${state.paying ? 'disabled' : ''}>
        ${state.paying ? '<span class="spin"></span> Confirming…' : 'Confirm booking &amp; email me the details'}
      </button>
      <p class="summary__note" style="text-align:center">${esc(POLICY_TEXT.taxes)} Demo email — not actually sent in this preview.</p>
    </div>`;
}

/* ---- Confirmation ----------------------------------------- */
function viewConfirmation(state) {
  const b = state.confirmed;
  const p = state.price;
  return `
    <div class="confirm">
      <div class="confirm__badge">${icons.spark}</div>
      <h2>You're booked, ${esc(state.customer.name.split(' ')[0] || 'lovely')}!</h2>
      <p style="color:var(--text-soft);margin-top:10px">Your booking summary is on its way to ${esc(state.customer.email)}. We can't wait to slay this for you.</p>
      <div class="confirm__ref">Booking ${esc(b.id)}</div>

      <div class="receipt">
        <h4>Appointment</h4>
        <div class="receipt__row"><span class="k">When</span><span>${labelDateLong(b.date)} · ${label12h(b.start)}</span></div>
        <div class="receipt__row"><span class="k">Duration</span><span>~${state.totalServiceMinutes} min</span></div>
        <div class="receipt__row"><span class="k">Where</span><span>${esc(BUSINESS.address)}</span></div>
        ${state.customRequest ? `<div class="receipt__row"><span class="k">Your request</span><span style="max-width:60%;text-align:right">${esc(state.customRequest)}</span></div>` : ''}
        ${state.notes ? `<div class="receipt__row"><span class="k">Further instructions</span><span style="max-width:60%;text-align:right">${esc(state.notes)}</span></div>` : ''}
        ${isRequestMode() ? `
          <div class="receipt__row"><span class="k">Estimated total${p.hasQuote ? ' (before quote)' : ''}</span><span>${money(p.estTotal)}</span></div>
          <div class="receipt__row"><span class="k">Payment</span><span>Pay in-store — nothing due now</span></div>`
        : `
          <div class="receipt__row"><span class="k">Deposit paid</span><span>${money(p.deposit)}</span></div>
          <div class="receipt__row"><span class="k">Balance in-store</span><span>${money(p.balanceDue)}</span></div>`}
      </div>

      <div class="divider"></div>
      <button class="btn btn--ghost" id="new-booking">Book another appointment</button>
    </div>`;
}

/* ---- Summary rail + bottom navbar ------------------------- */
function renderSummary(state) {
  const p = state.price;
  if (p.lines.length === 0) {
    return `<aside class="summary">
      <h3>Your visit</h3>
      <p class="summary__empty">No services selected yet. Pick a service to get started.</p>
    </aside>`;
  }
  const when = state.date && state.slot
    ? `<div class="summary__meta">${icons.clock}<span>${labelDate(state.date)} · ${label12h(state.slot)}</span></div>` : '';
  return `<aside class="summary">
    <h3>Your visit</h3>
    <div class="summary__sub">${icons.clock ? '' : ''}~${state.totalServiceMinutes} min · ${state.serviceIds.size} service${state.serviceIds.size !== 1 ? 's' : ''}</div>
    ${p.lines.map((l) => `
      <div class="sum-line ${l.kind === 'surcharge' ? 'surcharge' : ''}">
        <span class="lbl">${l.label}${l.note ? ` <small>${l.note}</small>` : ''}</span>
        <span class="amt">${lineAmount(l)}</span>
      </div>`).join('')}
    ${when}
    <div class="summary__totals">
      ${isRequestMode() ? `
        <div class="tot-row"><span>Est. subtotal</span><span>${money(p.subtotal)}${p.hasQuote ? ' +' : ''}</span></div>
        <div class="tot-row deposit"><span>Est. total</span><span class="amt">${money(p.estTotal)}</span></div>
        <div class="tot-row"><span>Due today</span><span>$0 — in-store</span></div>`
      : `
        <div class="tot-row"><span>Subtotal</span><span>${money(p.subtotal)}</span></div>
        <div class="tot-row deposit"><span>${p.depositLabel} today</span><span class="amt">${money(p.deposit)}</span></div>
        <div class="tot-row"><span>Balance in-store</span><span>${money(p.balanceDue)}</span></div>`}
    </div>
    <p class="summary__note">${isRequestMode() ? POLICY_TEXT.request : POLICY_TEXT.deposit}</p>
  </aside>`;
}

function renderNavbar(state) {
  const p = state.price;
  const last = STEPS.length - 1;
  const backLabel = state.step === 0 ? '' :
    `<button class="btn btn--ghost" id="nav-back">${icons.left} Back</button>`;
  let next = '';
  if (state.step < last) {
    const disabled = !state.canAdvance;
    next = `<button class="btn btn--primary" id="nav-next" ${disabled ? 'disabled' : ''}>Continue ${icons.right}</button>`;
  }
  const totalLbl = isRequestMode() ? 'Estimated total' : (p.lines.length ? 'Deposit today' : 'Total');
  const totalVal = isRequestMode()
    ? `${money(p.estTotal)}${p.hasQuote ? '+' : ''}`
    : money(p.deposit);
  return `<div class="navbar"><div class="wrap navbar__inner">
    <div class="navbar__total">
      <div class="lbl">${totalLbl}</div>
      <div class="val">${totalVal}</div>
    </div>
    ${backLabel}
    ${next}
  </div></div>`;
}

/* ===== js/app.js ===== */

/**
 * app.js — the booking controller.
 * Owns state, derives pricing/validation, renders the active step, and wires
 * every interaction. Deliberately one small state machine: predictable and
 * easy to lift into a Shopify section or a framework later.
 */










const now = new Date();
// Staff mode: a service agent taking the booking can hand-set the duration.
const staffMode = new URLSearchParams(location.search).get(STAFF.urlParam) === '1';

const state = {
  step: 0,
  staff: staffMode,
  durationOverride: null,   // staff-entered minutes; null = auto
  serviceIds: new Set(),
  addonIds: new Set(),
  isLong: false,
  customRequest: '',
  notes: '',
  calMonth: startOfMonth(now),
  date: null,
  slot: null,
  customer: { name: '', email: '', phone: '' },
  errors: {},
  bookings: [],
  paying: false,
  confirmed: null,
  now,
};

// Root the app queries against: `document` when standalone, or a shadow root
// when embedded (e.g. inside a Shopify theme section). Set by mountApp().
let $root = document;
let $app = null;
let _lastScrollKey = null;

/* ---- Derived values -------------------------------------- */
// The menu-driven estimate from the selected services + add-ons.
function autoServiceMinutes() {
  let mins = 0;
  for (const id of state.serviceIds) mins += getService(id)?.durationMin || 0;
  for (const id of state.addonIds) mins += getAddon(id)?.durationMin || 0;
  return mins;
}
// What the calendar actually uses: a staff override when set, else the auto
// estimate. Everything scheduling-related reads this so the two never drift.
function effectiveMinutes() {
  return state.staff && state.durationOverride != null
    ? state.durationOverride
    : autoServiceMinutes();
}

function computeCanAdvance() {
  switch (state.step) {
    case 0: return state.serviceIds.size >= 1;
    case 1: return true;
    case 2: return !!(state.date && state.slot);
    // Step 3: keep Continue enabled so we can show inline errors on click
    // (a disabled button gives the client no clue what's missing).
    case 3: return true;
    default: return true;
  }
}

// When the open-ended "Something else" service is chosen, we need the client
// to actually describe it — otherwise the request is meaningless to the salon.
function validateCustomRequest(setErrors) {
  const needs = state.serviceIds.has('custom-request') && !state.customRequest.trim();
  if (setErrors) state.errors = { ...state.errors, customRequest: needs ? 'Please tell us what you\'d like done.' : '' };
  return !needs;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function validateCustomer(setErrors) {
  const e = {};
  if (!state.customer.name.trim()) e.name = 'Please enter your name.';
  if (!EMAIL_RE.test(state.customer.email.trim())) e.email = 'Enter a valid email.';
  const digits = state.customer.phone.replace(/\D/g, '');
  if (digits.length < 10) e.phone = 'Enter a valid phone number.';
  if (setErrors) state.errors = e;
  return Object.keys(e).length === 0;
}

/* ---- Render ---------------------------------------------- */
function render() {
  // Recompute derived state used across views.
  state.autoServiceMinutes = autoServiceMinutes();
  state.totalServiceMinutes = effectiveMinutes();
  state.price = priceBooking({
    serviceIds: [...state.serviceIds],
    addonIds: [...state.addonIds],
    isLong: state.isLong,
  });
  state.canAdvance = computeCanAdvance();

  if (state.confirmed) {
    $app.innerHTML = `<div class="wrap">${viewConfirmation(state)}</div>`;
    wireConfirmation();
    maybeScroll();
    return;
  }

  // The final step depends on the booking mode: pay a deposit, or (no payment)
  // review + confirm and get the summary emailed.
  const finalView = isRequestMode() ? viewConfirm : viewDeposit;
  const stepView = [viewServices, viewOptions, viewDateTime, viewDetails, finalView][state.step](state);
  const staffBanner = state.staff
    ? `<div class="staff-banner"><span class="pill">Staff mode</span> Taking this booking on the client's behalf — you can set the appointment duration by hand on the next step.</div>`
    : '';
  $app.innerHTML = `
    <div class="wrap">
      ${staffBanner}
      ${renderStepper(state.step)}
      <div class="layout">
        <div>${stepView}</div>
        ${renderSummary(state)}
      </div>
    </div>
    ${renderNavbar(state)}`;

  wireStep();
  maybeScroll();
}

// Scroll to the top of the flow only when the step actually changes — never on
// the first render (don't yank the page on mount) and not on in-step toggles.
function maybeScroll() {
  const key = state.confirmed ? 'confirmed' : `step-${state.step}`;
  if (key === _lastScrollKey) return;
  const first = _lastScrollKey === null;
  _lastScrollKey = key;
  if (first) return;
  if ($root === document) window.scrollTo({ top: 0, behavior: 'smooth' });
  else if ($app && $app.scrollIntoView) $app.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/* ---- Navigation ------------------------------------------ */
/**
 * Point the calendar at the first month that actually has an open day for the
 * current selection, so clients never land on an empty month (e.g. when today
 * is the last open day of the month). Bounded by the max-advance window.
 */
function ensureAvailableMonth() {
  const mins = effectiveMinutes();
  const cursor = new Date(state.now.getFullYear(), state.now.getMonth(), 1);
  const maxDay = addDays(state.now, SCHEDULING.maxAdvanceDays);
  const lastMonth = new Date(maxDay.getFullYear(), maxDay.getMonth(), 1);
  while (cursor <= lastMonth) {
    const dim = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    for (let d = 1; d <= dim; d++) {
      const key = dateKey(new Date(cursor.getFullYear(), cursor.getMonth(), d));
      if (isDayBookable(key, state.now) && dayHasAvailability(key, mins, state.bookings, state.now)) {
        state.calMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
        return;
      }
    }
    cursor.setMonth(cursor.getMonth() + 1);
  }
}

function goNext() {
  // Options step: if a custom request is selected, require its description.
  if (state.step === 1 && !validateCustomRequest(true)) { render(); return; }
  if (state.step === 3 && !validateCustomer(true)) { render(); return; }
  if (!computeCanAdvance()) return;
  if (state.step === 1 && !state.date) ensureAvailableMonth(); // entering Date step
  if (state.step < STEPS.length - 1) { state.step++; render(); }
}
function goBack() {
  if (state.step > 0) { state.step--; render(); }
}

/* ---- Event wiring per step ------------------------------- */
function wireStep() {
  // Bottom nav
  byId('nav-next')?.addEventListener('click', goNext);
  byId('nav-back')?.addEventListener('click', goBack);

  // Step-specific
  if (state.step === 0) wireServices();
  if (state.step === 1) wireOptions();
  if (state.step === 2) wireDateTime();
  if (state.step === 3) wireDetails();
  if (state.step === 4) wireFinal();
}

function wireServices() {
  $app.querySelectorAll('[data-service]').forEach((el) =>
    el.addEventListener('click', () => {
      const id = el.dataset.service;
      state.serviceIds.has(id) ? state.serviceIds.delete(id) : state.serviceIds.add(id);
      render();
    }));
}

function wireOptions() {
  $app.querySelectorAll('[data-length]').forEach((el) =>
    el.addEventListener('click', () => { state.isLong = el.dataset.length === 'long'; render(); }));
  $app.querySelectorAll('[data-addon]').forEach((el) =>
    el.addEventListener('click', () => {
      const id = el.dataset.addon;
      state.addonIds.has(id) ? state.addonIds.delete(id) : state.addonIds.add(id);
      render();
    }));
  // Staff duration override. Update silently on input (keeps focus while
  // typing); re-render on change/blur so the calendar + summary refresh.
  const dur = byId('staff-duration');
  if (dur) {
    dur.addEventListener('input', (e) => { state.durationOverride = parseDuration(e.target.value); });
    dur.addEventListener('change', () => { state.durationOverride = clampDuration(state.durationOverride); render(); });
  }
  byId('staff-duration-reset')?.addEventListener('click', () => { state.durationOverride = null; render(); });

  // Custom request + notes: update silently (no re-render → keeps focus).
  byId('custom-req')?.addEventListener('input', (e) => {
    state.customRequest = e.target.value;
    if (state.errors.customRequest && e.target.value.trim()) {
      state.errors.customRequest = '';
      const err = byId('custom-req').parentElement.querySelector('.input-err');
      if (err) err.textContent = '';
    }
  });
  byId('notes')?.addEventListener('input', (e) => { state.notes = e.target.value; });
}

function wireDateTime() {
  $app.querySelectorAll('[data-month]').forEach((el) =>
    el.addEventListener('click', () => {
      const delta = Number(el.dataset.month);
      state.calMonth = new Date(state.calMonth.getFullYear(), state.calMonth.getMonth() + delta, 1);
      render();
    }));
  $app.querySelectorAll('[data-day]').forEach((el) =>
    el.addEventListener('click', () => {
      if (el.disabled) return;
      state.date = el.dataset.day;
      state.slot = null; // reset time when day changes
      render();
    }));
  $app.querySelectorAll('[data-slot]').forEach((el) =>
    el.addEventListener('click', () => { if (!el.disabled) { state.slot = el.dataset.slot; render(); } }));
}

function wireDetails() {
  const bind = (id, key) => byId(id)?.addEventListener('input', (e) => {
    state.customer[key] = e.target.value;
    // Live-clear an error once the field looks valid, without full re-render.
    if (state.errors[key]) { state.errors[key] = ''; const err = byId(id).parentElement.querySelector('.input-err'); if (err) err.textContent = ''; }
  });
  bind('c-name', 'name');
  bind('c-email', 'email');
  bind('c-phone', 'phone');
}

function wireFinal() {
  byId('pay-btn')?.addEventListener('click', payAndBook);       // deposit mode
  byId('confirm-btn')?.addEventListener('click', confirmAndEmail); // request mode
}

function wireConfirmation() {
  byId('new-booking')?.addEventListener('click', resetFlow);
}

/* ---- Booking finalisers ---------------------------------- */
// Shared draft both modes create before diverging on payment vs. email.
function buildDraft() {
  const p = state.price;
  return {
    date: state.date,
    start: state.slot,
    durationMin: state.totalServiceMinutes + 15, // + buffer (mirror config)
    durationOverride: state.staff ? state.durationOverride : null,
    bookedBy: state.staff ? 'staff' : 'client',
    serviceIds: [...state.serviceIds],
    addonIds: [...state.addonIds],
    isLong: state.isLong,
    customRequest: state.customRequest,
    notes: state.notes,
    customer: { ...state.customer },
    mode: BOOKING.mode,
    // Request-mode bookings need no payment, so they're confirmed immediately;
    // deposit-mode bookings wait for the deposit before confirming.
    status: isRequestMode() ? 'confirmed' : 'pending_deposit',
    pricing: {
      subtotal: p.subtotal, estTotal: p.estTotal, hasQuote: p.hasQuote,
      deposit: p.deposit, balanceDue: p.balanceDue,
    },
    currency: BUSINESS.currency,
  };
}

// Deposit mode: create booking, take deposit, mark paid.
async function payAndBook() {
  if (state.paying) return;
  state.paying = true; render();
  const p = state.price;
  try {
    const record = await activeStore.createBooking(buildDraft());
    const payment = await activePayment.payDeposit({
      amount: p.deposit, currency: BUSINESS.currency, booking: record,
    });
    if (!payment.ok) throw new Error('Payment failed');
    state.confirmed = (await activeStore.markDepositPaid(record.id, payment)) || record;
    state.bookings = await activeStore.listBookings();
  } catch (err) {
    console.error(err);
    alert('Something went wrong securing your booking. Please try again.');
  } finally {
    state.paying = false;
    render();
  }
}

// Request mode: create booking (no payment) and email the summary.
async function confirmAndEmail() {
  if (state.paying) return;
  state.paying = true; render();
  const p = state.price;
  try {
    const record = await activeStore.createBooking(buildDraft());
    // Email is best-effort: the booking is already saved, so a mail failure
    // must never block the confirmation the client sees.
    try {
      const text = bookingSummaryText({
        business: BUSINESS,
        dateLabel: labelDateLong(state.date),
        timeLabel: label12h(state.slot),
        lines: p.lines,
        price: p,
        customRequest: state.customRequest,
        notes: state.notes,
        customer: state.customer,
      });
      await activeNotify.sendConfirmation({
        booking: record,
        to: state.customer.email,
        ownerTo: BOOKING.notifyOwnerTo,
        text,
      });
    } catch (mailErr) {
      console.warn('[Bellar] confirmation email failed (booking still saved):', mailErr);
    }
    state.confirmed = record;
    state.bookings = await activeStore.listBookings();
  } catch (err) {
    console.error(err);
    alert('Something went wrong confirming your booking. Please try again.');
  } finally {
    state.paying = false;
    render();
  }
}

function resetFlow() {
  Object.assign(state, {
    step: 0, durationOverride: null,
    serviceIds: new Set(), addonIds: new Set(), isLong: false,
    customRequest: '', notes: '',
    calMonth: startOfMonth(new Date()), date: null, slot: null,
    customer: { name: '', email: '', phone: '' }, errors: {}, paying: false, confirmed: null,
  });
  render();
}

/* ---- Utils ----------------------------------------------- */
function byId(id) { return $root.getElementById(id); }

// Parse a hand-typed duration; empty/invalid => null (revert to auto).
function parseDuration(v) {
  const n = parseInt(String(v).trim(), 10);
  return Number.isFinite(n) ? n : null;
}
// Keep a hand-typed duration within sane guardrails.
function clampDuration(n) {
  if (n == null) return null;
  return Math.min(Math.max(n, STAFF.minDurationMin), STAFF.maxDurationMin);
}

/* ---- Boot ------------------------------------------------ */
async function init() {
  try { state.bookings = await activeStore.listBookings(); } catch { state.bookings = []; }
  render();
}

// Mount into a root: `document` for the standalone site, or a shadow root for
// an embedded host (Shopify section). Keeps the app fully self-contained.
function mountApp(root) {
  $root = root;
  $app = root.getElementById('app');
  if (!$app) { console.warn('[Bellar] no #app element found in mount root'); return; }
  init();
}

// Expose a mount entry point for embedded hosts (the Shopify section calls this
// with a shadow root); harmless in the standalone build.
if (typeof window !== 'undefined') window.BellarBooking = { mount: mountApp };

// Standalone auto-mount: index.html sets this flag before loading the app.
// (In an embed, #app lives in a shadow root, so the host calls mount() itself.)
if (typeof window !== 'undefined' && window.__BELLAR_STANDALONE__) mountApp(document);

})();
